import { useRef, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';

export const AUDIO_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
  video: false,
};

// Free STUN/TURN servers to allow connections across different networks (WiFi, 4G/5G mobile data, firewalls)
export const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    // Public free TURN relay servers (by Metered.ca with TCP & UDP fallbacks)
    {
      urls: [
        'turn:openrelay.metered.ca:80',
        'turn:openrelay.metered.ca:443',
        'turn:openrelay.metered.ca:443?transport=tcp'
      ],
      username: 'openrelay',
      credential: 'openrelay',
    },
  ],
  iceCandidatePoolSize: 10,
};

export function useWebRTCCall({ socketRef, onCallEnded, onCallConnected, onCallDeclined, onCallTimeout }) {
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const ringTimeoutRef = useRef(null);
  
  const [callState, setCallState] = useState('idle'); // idle | calling | ringing | connected | ended
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [peerSocketId, setPeerSocketId] = useState(null);
  const durationTimerRef = useRef(null);

  // Initialize hidden remote audio element
  useEffect(() => {
    if (!remoteAudioRef.current) {
      const audio = document.createElement('audio');
      audio.autoplay = true;
      audio.playsInline = true;
      audio.style.display = 'none';
      document.body.appendChild(audio);
      remoteAudioRef.current = audio;
    }

    return () => {
      if (remoteAudioRef.current && remoteAudioRef.current.parentNode) {
        remoteAudioRef.current.parentNode.removeChild(remoteAudioRef.current);
        remoteAudioRef.current = null;
      }
    };
  }, []);

  const startDurationTimer = useCallback(() => {
    setCallDuration(0);
    if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    durationTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        try { track.stop(); } catch (e) {}
      });
      localStreamRef.current = null;
    }

    if (peerRef.current) {
      try { peerRef.current.close(); } catch (e) {}
      peerRef.current = null;
    }

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    const socket = socketRef?.current;
    if (socket) {
      socket.off('ice_candidate');
      socket.off('webrtc_offer');
      socket.off('webrtc_answer');
      socket.off('call_accepted');
      socket.off('call_rejected');
      socket.off('call_ended');
    }

    stopDurationTimer();
    setCallState('ended');
    setPeerSocketId(null);
    setIsMuted(false);
  }, [socketRef, stopDurationTimer]);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, []);

  const createPeer = useCallback((targetSocketId) => {
    const peer = new RTCPeerConnection(ICE_SERVERS);

    peer.onicecandidate = (event) => {
      const socket = socketRef?.current;
      if (event.candidate && socket) {
        socket.emit('ice_candidate', { targetSocketId, candidate: event.candidate });
      }
    };

    peer.ontrack = (event) => {
      console.log('🎵 Remote audio track received:', event.streams[0]);
      if (remoteAudioRef.current && event.streams[0]) {
        remoteAudioRef.current.srcObject = event.streams[0];
        remoteAudioRef.current.play().catch(err => {
          console.warn('Auto-play blocked, user interaction required:', err);
        });
      }
    };

    let disconnectRecoveryTimer = null;

    peer.onconnectionstatechange = () => {
      console.log('📶 WebRTC peer connectionState:', peer.connectionState);
      if (peer.connectionState === 'connected') {
        if (disconnectRecoveryTimer) {
          clearTimeout(disconnectRecoveryTimer);
          disconnectRecoveryTimer = null;
        }
        setCallState('connected');
        startDurationTimer();
        onCallConnected?.();
      } else if (peer.connectionState === 'disconnected') {
        console.warn('⚠️ WebRTC transient disconnect detected. Allowing 15s to recover network stream...');
        if (!disconnectRecoveryTimer) {
          disconnectRecoveryTimer = setTimeout(() => {
            if (peerRef.current && peerRef.current.connectionState === 'disconnected') {
              console.log('❌ WebRTC disconnect timeout reached. Cleaning up call.');
              cleanup();
              onCallEnded?.();
            }
          }, 15000);
        }
      } else if (['failed', 'closed'].includes(peer.connectionState)) {
        if (disconnectRecoveryTimer) clearTimeout(disconnectRecoveryTimer);
        cleanup();
        onCallEnded?.();
      }
    };

    peer.oniceconnectionstatechange = () => {
      console.log('🧊 WebRTC ICE connectionState:', peer.iceConnectionState);
      if (peer.iceConnectionState === 'connected' || peer.iceConnectionState === 'completed') {
        setCallState('connected');
        startDurationTimer();
        onCallConnected?.();
      }
    };

    peerRef.current = peer;
    return peer;
  }, [socketRef, cleanup, startDurationTimer, onCallConnected, onCallEnded]);

  // 1. CALLER: Initiate outgoing call
  const startCall = useCallback(async ({
    targetUserId,
    targetName,
    callerName,
    callerPhone,
    callerRole,
    projectId,
    reason,
    isIntercom = false
  }) => {
    const socket = socketRef?.current;
    if (!socket) {
      toast.error('Voice network not connected yet. Please try again.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
      localStreamRef.current = stream;
      setCallState('calling');

      // 10-second Ringing Timeout: client hears luxury orchestral waiting melody + brand announcement.
      // If team does not answer within 10s, cancel ringing and trigger onCallTimeout so Epic AI takes over!
      if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = setTimeout(() => {
        console.log('⏰ 10s ringing timeout reached - team did not answer.');
        ringTimeoutRef.current = null;
        const liveSocket = socketRef?.current;
        if (liveSocket) {
          liveSocket.emit('call_cancelled');
        }
        cleanup();
        onCallTimeout?.();
      }, 10000);

      socket.emit('call_request', {
        targetUserId,
        targetName,
        callerName,
        callerPhone,
        callerRole,
        projectId,
        reason,
        isIntercom
      });

      // Target accepted call
      socket.once('call_accepted', async ({ peerSocketId: targetSid }) => {
        if (ringTimeoutRef.current) {
          clearTimeout(ringTimeoutRef.current);
          ringTimeoutRef.current = null;
        }

        const liveSocket = socketRef?.current;
        if (!liveSocket) return;

        console.log('📞 Call accepted by receiver:', targetSid);
        setPeerSocketId(targetSid);
        const peer = createPeer(targetSid);
        stream.getTracks().forEach(track => peer.addTrack(track, stream));

        // Listen for WebRTC Answer from receiver
        liveSocket.once('webrtc_answer', async ({ answer }) => {
          console.log('✅ Caller received webrtc_answer from receiver');
          try {
            await peer.setRemoteDescription(new RTCSessionDescription(answer));
          } catch (e) {
            console.error('Error setting remote description on caller:', e);
          }
        });

        // Listen for ICE Candidates from receiver
        liveSocket.on('ice_candidate', async ({ candidate }) => {
          if (candidate && peerRef.current) {
            try {
              await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (e) {
              console.warn('Error adding ICE candidate on caller:', e);
            }
          }
        });

        // Create offer and send to receiver
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        liveSocket.emit('webrtc_offer', { targetSocketId: targetSid, offer });
      });

      // Target rejected call
      socket.once('call_rejected', () => {
        if (ringTimeoutRef.current) {
          clearTimeout(ringTimeoutRef.current);
          ringTimeoutRef.current = null;
        }
        cleanup();
        onCallDeclined?.();
      });

      // Call ended by peer
      socket.once('call_ended', () => {
        if (ringTimeoutRef.current) {
          clearTimeout(ringTimeoutRef.current);
          ringTimeoutRef.current = null;
        }
        toast('Call ended.', { icon: '📴' });
        cleanup();
        onCallEnded?.();
      });

    } catch (err) {
      if (ringTimeoutRef.current) {
        clearTimeout(ringTimeoutRef.current);
        ringTimeoutRef.current = null;
      }
      console.error('Failed to access microphone:', err);
      toast.error('Microphone access denied or not available. Please allow mic permissions.');
      cleanup();
    }
  }, [socketRef, createPeer, cleanup, onCallEnded, onCallDeclined, onCallTimeout]);

  // 2. RECEIVER: Answer incoming call (or answer takeover)
  const answerCall = useCallback(async (callerSocketId, isTakeover = false) => {
    const socket = socketRef?.current;
    if (!socket) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
      localStreamRef.current = stream;
      setPeerSocketId(callerSocketId);
      setCallState('connected');
      startDurationTimer();

      const peer = createPeer(callerSocketId);
      stream.getTracks().forEach(track => peer.addTrack(track, stream));

      // Listen for incoming offer from caller
      socket.once('webrtc_offer', async ({ offer }) => {
        console.log('✅ Receiver received webrtc_offer from caller');
        try {
          await peer.setRemoteDescription(new RTCSessionDescription(offer));
          const answer = await peer.createAnswer();
          await peer.setLocalDescription(answer);
          socketRef?.current?.emit('webrtc_answer', { targetSocketId: callerSocketId, answer });
        } catch (e) {
          console.error('Error creating answer on receiver:', e);
        }
      });

      // Listen for ICE Candidates from caller
      socket.on('ice_candidate', async ({ candidate }) => {
        if (candidate && peerRef.current) {
          try {
            await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (e) {
            console.warn('Error adding ICE candidate on receiver:', e);
          }
        }
      });

      // Notify caller that receiver clicked accept (if standard call)
      if (!isTakeover) {
        socket.emit('call_accepted', { callerSocketId });
      }

      socket.once('call_ended', () => {
        toast('Call ended.', { icon: '📴' });
        cleanup();
        onCallEnded?.();
      });

    } catch (err) {
      console.error('Failed to answer call:', err);
      toast.error('Could not access microphone to answer call.');
      cleanup();
    }
  }, [socketRef, createPeer, cleanup, startDurationTimer, onCallEnded]);

  // 3. CALLER HANDOFF: Transfer active AI call to Admin WebRTC
  const transferToPeer = useCallback(async (targetSid) => {
    const socket = socketRef?.current;
    if (!socket) return;

    try {
      let stream = localStreamRef.current;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
        localStreamRef.current = stream;
      }
      setPeerSocketId(targetSid);
      setCallState('connected');
      startDurationTimer();

      const peer = createPeer(targetSid);
      stream.getTracks().forEach(track => peer.addTrack(track, stream));

      // Listen for WebRTC Answer from admin
      socket.once('webrtc_answer', async ({ answer }) => {
        console.log('✅ Handoff received webrtc_answer from admin');
        try {
          await peer.setRemoteDescription(new RTCSessionDescription(answer));
        } catch (e) {
          console.error('Error setting remote description on handoff:', e);
        }
      });

      // Listen for ICE Candidates from admin
      socket.on('ice_candidate', async ({ candidate }) => {
        if (candidate && peerRef.current) {
          try {
            await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (e) {
            console.warn('Error adding ICE candidate on handoff:', e);
          }
        }
      });

      // Send offer to admin
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      socket.emit('webrtc_offer', { targetSocketId: targetSid, offer });

      socket.once('call_ended', () => {
        toast('Call ended.', { icon: '📴' });
        cleanup();
        onCallEnded?.();
      });
    } catch (err) {
      console.error('Transfer failed:', err);
      toast.error('Failed to connect audio with support.');
    }
  }, [socketRef, createPeer, cleanup, startDurationTimer, onCallEnded]);

  // 4. RECEIVER: Decline incoming call
  const rejectCall = useCallback((callerSocketId) => {
    const socket = socketRef?.current;
    if (socket && callerSocketId) {
      socket.emit('call_rejected', { callerSocketId });
    }
    setCallState('idle');
  }, [socketRef]);

  // 5. Hang up active call
  const endCall = useCallback(() => {
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    const socket = socketRef?.current;
    if (socket) {
      if (peerSocketId) {
        socket.emit('call_ended', { peerSocketId });
      } else {
        socket.emit('call_cancelled');
      }
    }
    cleanup();
    onCallEnded?.();
  }, [socketRef, peerSocketId, cleanup, onCallEnded]);

  return {
    callState,
    setCallState,
    callDuration,
    isMuted,
    toggleMute,
    startCall,
    answerCall,
    transferToPeer,
    rejectCall,
    endCall,
    peerSocketId,
    cleanup
  };
}
